import { useState } from 'react';
import { Form, Button, Input, message } from '@fuxi/eevee-ui';
import classnames from 'classnames/bind';
import { checkSpace } from '@commonPackage/utils';
import { useNECaptcha } from '@yaotai/common-utils';
import { useTranslation } from 'react-i18next';
import { CountryPhoneFieldFormItem } from '@commonPackage/components';
import { StyledButton } from '@portalCommon/styles/utils';
import { CDN } from '@portalCommon/constants/cdn';
import {
  checkInternationalPhone,
  checkInternationalPhoneRequired,
  checkMaxLength,
  getPostBody,
  reportContactCommitFrom,
} from '@portalCommon/utils';
import useReferralCode from '@portalCommon/hooks/useReferralCode';
import { ReferralInvalidToast } from '@portalCommon/components/ReferralInvalidToast';
import { useDebounceFn } from '@fuxi/eevee-hooks';
import API from '@/services';

import styles from './index.module.less';

const cx = classnames.bind(styles);

export const ReferralBanner = () => {
  const { code, isFromReferral } = useReferralCode();
  const [isBannerClosed, setIsBannerClosed] = useState(false);
  const [isEditting, setIsEditting] = useState(false);
  const [form] = Form.useForm();

  const [isContactSuccess, setIsContactSuccess] = useState<boolean | undefined>();
  const [messageApi, messageContextHolder] = message.useMessage();
  const { captchaIns, getCaptchaIns } = useNECaptcha({
    mode: 'bind',
    element: '#captcha_activity',
    onVerify: function (err: any, data: any) {
      if (err) return;
      handleFinish(data.validate);
    },
  });

  const { t } = useTranslation();

  const consult = () => {
    setIsEditting(true);
  };

  const handleFinish = async (validate: string) => {
    const values = await form.validateFields();
    values.discover = {
      values: [[t('consultForm.knownFrom.answer4'), code]],
    };
    const body = getPostBody(values, 'activity_advisory');
    try {
      const { data } = await API.contact.contact({ ...body, validate });
      setIsContactSuccess(true);
      setIsEditting(false);
      reportContactCommitFrom();

      if (data?.show_referrer_invalid_tip) {
        messageApi.warning(ReferralInvalidToast(), 5);
      }
    } catch (err) {
      setIsContactSuccess(false);
      getCaptchaIns()?.refresh();
    }
  };

  const handleCaptcha = async () => {
    await form.validateFields();
    captchaIns?.verify();
  };
  const { run } = useDebounceFn(
    () => {
      handleCaptcha?.();
    },
    {
      wait: 500,
    },
  );

  const closeBanner = () => {
    setIsBannerClosed(true);
  };

  return isFromReferral && !isBannerClosed ? (
    <div className={cx('banner-container')}>
      <div className={cx('close-button')} onClick={closeBanner}>
        <img src={CDN.closeButton} alt="x"></img>
      </div>
      <div id="captcha_activity"></div>
      {messageContextHolder}
      {isEditting ? (
        <div className={cx('content-container')}>
          <Form form={form} layout="vertical" initialValues={{ referrer_code: code }} className={cx('form')}>
            <Form.Item
              name="name"
              label={<label className={cx('form-label')}>您的称呼</label>}
              validateFirst
              rules={[
                {
                  required: true,
                  message: t('consultForm.nameValidator'),
                },
                {
                  validator: checkSpace(t('consultForm.nameValidator')),
                },
                {
                  validator: checkMaxLength(30, t('consultForm.nameLenValid')),
                },
              ]}
              className={cx('form-item', 'name')}>
              <Input className={cx('form-input')} placeholder={t('consultForm.namePlaceHolder')} />
            </Form.Item>
            <CountryPhoneFieldFormItem
              form={form}
              name="phone"
              labelName={<label className={cx('form-label')}>您的电话</label>}
              validateTrigger={[]}
              rules={[
                {
                  required: true,
                  message: t('consultForm.telValidator'),
                },
                {
                  validator: checkInternationalPhoneRequired(t('consultForm.telValidator')),
                },
                {
                  validator: checkInternationalPhone(true, t('consultForm.telValidFormat')),
                },
              ]}
              placeholder={t('consultForm.telPlaceHolder')}
              className={cx('form-item', 'phone')}
            />
            <Form.Item
              name="orga_name"
              label={<label className={cx('form-label')}>组织/公司</label>}
              validateFirst
              rules={[
                {
                  required: true,
                  message: t('consultForm.orgValidator'),
                },
                {
                  validator: checkSpace(t('consultForm.orgValidator')),
                },
                {
                  max: 50,
                  message: t('consultForm.orgLenValid'),
                },
              ]}
              className={cx('form-item', 'orga-name')}>
              <Input className={cx('form-input')} placeholder={t('consultForm.orgPlaceHolder')} />
            </Form.Item>
            <Form.Item
              name="title"
              label={<label className={cx('form-label')}>职位</label>}
              className={cx('form-item', 'title')}>
              <Input className={cx('form-input')} placeholder={t('consultForm.posPlaceHolder')} />
            </Form.Item>
            <Form.Item
              name="referrer_code"
              required
              label={<label className={cx('form-label')}>好友推荐码</label>}
              initialValue={code}
              className={cx('form-item', 'referral-code')}>
              <Input className={cx('form-input')} disabled />
            </Form.Item>
            <Form.Item label=" " className={cx('form-item', 'submit-item')}>
              <StyledButton styleType="primary" className={cx('btn')} onClick={() => run()}>
                提交
              </StyledButton>
            </Form.Item>
          </Form>
        </div>
      ) : (
        <div className={cx('content-container')}>
          {!isContactSuccess ? (
            <>
              <div className={cx('text-content')}>
                <div className={cx('main-content')}>好友推荐你使用网易瑶台，咨询可享专属服务</div>
                <div className={cx('side-content')}>好友推荐码：{code}</div>
              </div>
              <div className={cx('consult-button')}>
                <Button className={cx('image-button')} onClick={consult}>
                  立 即 咨 询
                </Button>
              </div>
            </>
          ) : (
            <div className={cx('text-content')}>
              <div className={cx('main-content')}>已收到您的咨询需求！</div>
              <div className={cx('side-content')}>
                专属顾问会在 1 个工作日内与您联系，并提供活动演示指导和定制方案！
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  ) : null;
};
